import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import { useListingActions } from '../../../hooks/useListingAdmin';
import { useOpeningHoursPreview } from '../../../hooks/useDirectorySearch';
import { HoursDayType, HoursEntry, HoursMode, TimeRule } from '../../../types';
import { MYZMANIM_FIELDS, SHABBOS_ENDS_MINUTES_AFTER_SUNSET } from '../../../config/constants';
import { LISTING_LIMITS, newId } from '../../../utils/listingEdit';
import {
  checkHoursDraft,
  DAYS_OF_WEEK,
  dayRuns,
  draftToHoursEntry,
  emptyHoursDraft,
  HOURS_SEASON_MODES,
  HOURS_SPECIAL_DAY_TYPES,
  HoursDraftKind,
  HoursEntryDraft,
  HoursSeasonMode,
  hoursEntryToDraft,
  sortHoursEntries,
} from '../../../utils/hoursForm';
import { pickLang } from '../../../utils/listingDisplay';
import { SHABBOS_ENDS_ZMAN } from '../../../utils/openingHours';
import { CANDLE_LIGHTING_ZMAN } from '../../../utils/timeUtils';
import WeekHours from '../../directory/WeekHours';
import TimeRuleFields from '../TimeRuleFields';
import HoursUpload from './HoursUpload';
import { BilingualField, Checkbox, ChipSelect, ErrorList, SectionTitle, SelectField } from './formFields';
import type { TabProps } from './ListingEditor';

const HOURS_MODES: HoursMode[] = ['scheduled', 'byAppointment', 'callFirst', 'alwaysOpen'];
const KINDS: HoursDraftKind[] = ['days', 'date'];

/** What's being edited: an existing entry's id, a new entry, or nothing. */
type Editing = { id: string | null; draft: HoursEntryDraft } | null;

export default function HoursTab({ listing }: TabProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const { updateListing, saving } = useListingActions();
  const [editing, setEditing] = useState<Editing>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [failed, setFailed] = useState(false);

  const stored = useMemo(() => listing.hours ?? [], [listing.hours]);
  const sorted = useMemo(() => sortHoursEntries(stored), [stored]);

  // Live preview: stored hours with the entry being edited applied.
  const previewHours = useMemo(() => {
    if (!editing || checkHoursDraft(editing.draft).length) return stored;
    const entry = draftToHoursEntry(editing.draft, editing.id ?? 'preview');
    return editing.id ? stored.map((h) => (h.id === editing.id ? entry : h)) : [...stored, entry];
  }, [stored, editing]);
  const preview = useOpeningHoursPreview(previewHours, 'scheduled');

  const write = async (patch: Record<string, unknown>) => {
    setFailed(false);
    try {
      await updateListing(listing.id, patch);
      return true;
    } catch {
      setFailed(true);
      return false;
    }
  };

  const saveEntry = async () => {
    if (!editing) return;
    const problems = checkHoursDraft(editing.draft);
    setErrors(problems);
    if (problems.length) return;
    const entry = draftToHoursEntry(editing.draft, editing.id ?? newId());
    const next = editing.id ? stored.map((h) => (h.id === editing.id ? entry : h)) : [...stored, entry];
    if (await write({ hours: next })) setEditing(null);
  };

  const removeEntry = async (id: string) => {
    if (!window.confirm(t('directory.admin.hours.confirmDelete'))) return;
    await write({ hours: stored.filter((h) => h.id !== id) });
  };

  const startEdit = (next: Editing) => {
    setErrors([]);
    setEditing(next);
  };

  const ruleText = (rule: TimeRule) => {
    if (rule.type === 'fixed') return rule.time;
    const sign = rule.offsetMinutes >= 0 ? '+' : '';
    const zman =
      rule.baseZman === SHABBOS_ENDS_ZMAN ? t('directory.admin.hours.shabbosEnds', { min: SHABBOS_ENDS_MINUTES_AFTER_SUNSET }) : rule.baseZman === CANDLE_LIGHTING_ZMAN ? t('zmanim.candleLighting') : MYZMANIM_FIELDS[rule.baseZman] || rule.baseZman;
    return `${zman} ${sign}${rule.offsetMinutes} ${t('admin.minutesShort')}`;
  };

  const whenText = (h: HoursEntry) => {
    if (h.specificDate) return h.specificDate;
    const special = h.dayTypes.filter((d) => d !== 'weekday');
    const days = h.daysOfWeek.length || special.length ? h.daysOfWeek : ([0, 1, 2, 3, 4, 5] as const);
    const dayText = dayRuns([...days]).map(([a, b]) =>
      a === b
        ? t(`directory.dayOfWeekShort.${a}`)
        : `${t(`directory.dayOfWeekShort.${a}`)}–${t(`directory.dayOfWeekShort.${b}`)}`
    );
    return [...dayText, ...special.map((d) => t(`dayTypes.${d}`))].join(', ');
  };

  const entryText = (h: HoursEntry) => (
    <div className="text-sm">
      <p className="font-medium text-ink">{whenText(h)}</p>
      <p className="text-ink" dir="ltr" style={{ textAlign: isHebrew ? 'right' : 'left' }}>
        {h.closed ? t('directory.admin.hours.closedLabel') : `${ruleText(h.open)} – ${ruleText(h.close)}`}
      </p>
      {h.season && <p className="text-xs text-ash">{t(`directory.admin.hours.season.${h.season}`)}</p>}
      {(h.validFrom || h.validTo) && (
        <p className="text-xs text-ash">
          {t('directory.admin.hours.seasonLabel', { from: h.validFrom || '…', to: h.validTo || '…' })}
        </p>
      )}
      {(h.noteHe || h.noteEn) && <p className="text-xs text-ash">{pickLang(h.noteEn, h.noteHe, isHebrew)}</p>}
    </div>
  );

  const usesSchedule = listing.hoursMode === 'scheduled';

  return (
    <div className="space-y-6">
      <SelectField
        label={t('directory.field.hoursMode')}
        info={t('directory.admin.hours.modeHint')}
        value={listing.hoursMode}
        onChange={(mode) => write({ hoursMode: mode })}
        options={HOURS_MODES.map((m) => ({ value: m, label: t(`directory.hoursMode.${m}`) }))}
      />
      {!usesSchedule && <p className="text-sm text-ash">{t(`directory.admin.hours.modeNote.${listing.hoursMode}`)}</p>}

      <section className="space-y-3">
        <SectionTitle>{t('directory.field.hours')}</SectionTitle>
        <p className="text-sm text-ash">{t('directory.admin.hours.hint')}</p>
        {sorted.length === 0 && <p className="text-sm text-ink">{t('directory.admin.hours.none')}</p>}
        <ul className="space-y-2">
          {sorted.map((h) =>
            editing?.id === h.id ? (
              <li key={h.id}>
                <EntryForm
                  draft={editing.draft}
                  onChange={(draft) => setEditing({ id: h.id, draft })}
                  onSave={saveEntry}
                  onCancel={() => startEdit(null)}
                  saving={saving}
                  errors={errors}
                />
              </li>
            ) : (
              <li key={h.id} className="rounded-[16px] border border-ink/10 p-3 flex items-start gap-2">
                <div className="min-w-0 flex-1">{entryText(h)}</div>
                <div className="flex shrink-0">
                  <IconButton label={t('common.edit')} onClick={() => startEdit({ id: h.id, draft: hoursEntryToDraft(h) })}>
                    <Pencil className="w-4 h-4" aria-hidden />
                  </IconButton>
                  <IconButton
                    label={t('directory.admin.hours.copy')}
                    onClick={() => startEdit({ id: null, draft: hoursEntryToDraft(h) })}
                    disabled={stored.length >= LISTING_LIMITS.hours}
                  >
                    <Copy className="w-4 h-4" aria-hidden />
                  </IconButton>
                  <IconButton label={t('common.delete')} onClick={() => removeEntry(h.id)} danger>
                    <Trash2 className="w-4 h-4" aria-hidden />
                  </IconButton>
                </div>
              </li>
            )
          )}
        </ul>

        {editing && editing.id === null ? (
          <EntryForm
            draft={editing.draft}
            onChange={(draft) => setEditing({ id: null, draft })}
            onSave={saveEntry}
            onCancel={() => startEdit(null)}
            saving={saving}
            errors={errors}
          />
        ) : (
          stored.length < LISTING_LIMITS.hours && (
            <button
              type="button"
              onClick={() => startEdit({ id: null, draft: emptyHoursDraft() })}
              className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-[10px] border border-ink/20 text-sm text-ink hover:border-ink/50"
            >
              <Plus className="w-4 h-4" aria-hidden />
              {t('directory.admin.hours.add')}
            </button>
          )
        )}
        {failed && <p className="text-sm text-danger-600">{t('directory.admin.saveFailed')}</p>}
      </section>

      <section className="space-y-2 pt-2 border-t border-ink/10">
        <SectionTitle>{t('directory.admin.hours.previewTitle')}</SectionTitle>
        <p className="text-xs text-ash">{t('directory.admin.hours.previewHint')}</p>
        <WeekHours week={preview.week} />
      </section>

      <HoursUpload hours={stored} onReplace={(hours) => write({ hours })} saving={saving} renderEntry={entryText} />
    </div>
  );
}

function IconButton({
  label,
  onClick,
  children,
  disabled,
  danger,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center w-11 h-11 rounded-[10px] hover:bg-fog disabled:opacity-30 ${
        danger ? 'text-danger-600' : 'text-ink'
      }`}
    >
      {children}
    </button>
  );
}

function EntryForm({
  draft,
  onChange,
  onSave,
  onCancel,
  saving,
  errors,
}: {
  draft: HoursEntryDraft;
  onChange: (draft: HoursEntryDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  errors: string[];
}) {
  const { t } = useTranslation();
  const set = (patch: Partial<HoursEntryDraft>) => onChange({ ...draft, ...patch });
  const hoursZmanim = [
    { key: CANDLE_LIGHTING_ZMAN, label: t('zmanim.candleLighting') },
    { key: SHABBOS_ENDS_ZMAN, label: t('directory.admin.hours.shabbosEnds', { min: SHABBOS_ENDS_MINUTES_AFTER_SUNSET }) },
  ];

  return (
    <div className="bg-fog rounded-[16px] border border-ink/10 p-4 space-y-4">
      <fieldset>
        <legend className="text-sm font-medium text-ink mb-1">{t('directory.admin.hours.kindTitle')}</legend>
        <div className="flex flex-wrap gap-2">
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={draft.kind === k}
              onClick={() => set({ kind: k })}
              className={`min-h-[44px] px-3 rounded-full border text-sm ${
                draft.kind === k ? 'bg-ink text-paper border-ink' : 'bg-paper text-ink border-ink/20 hover:border-ink/50'
              }`}
            >
              {t(`directory.admin.hours.kind.${k}`)}
            </button>
          ))}
        </div>
      </fieldset>

      {draft.kind === 'days' && (
        <>
          <ChipSelect
            label={t('directory.field.daysOfWeek')}
            info={t('directory.admin.hours.daysHint')}
            options={DAYS_OF_WEEK.map((d) => ({ value: d, label: t(`directory.dayOfWeekShort.${d}`) }))}
            selected={draft.daysOfWeek}
            onChange={(daysOfWeek) => set({ daysOfWeek })}
          />
          <ChipSelect<HoursDayType>
            label={t('directory.field.dayTypes')}
            info={t('directory.admin.hours.specialHint')}
            options={HOURS_SPECIAL_DAY_TYPES.map((d) => ({ value: d, label: t(`dayTypes.${d}`) }))}
            selected={draft.dayTypes}
            onChange={(dayTypes) => set({ dayTypes })}
          />
          <p className="text-xs text-ash">{t('directory.admin.hours.anyOfHint')}</p>
        </>
      )}
      {draft.kind === 'date' && (
        <div>
          <label htmlFor="hours-date" className="block text-sm font-medium text-ink mb-1">
            {t('directory.field.specificDate')}
          </label>
          <input
            id="hours-date"
            type="date"
            value={draft.specificDate}
            onChange={(e) => set({ specificDate: e.target.value })}
            className="min-h-[44px] px-3 py-2 rounded-[10px] border border-ink bg-paper text-ink text-sm"
          />
        </div>
      )}

      <Checkbox label={t('directory.field.closed')} checked={draft.closed} onChange={(closed) => set({ closed })} />

      {!draft.closed && (
        <>
          <div className="space-y-3 rounded-[10px] bg-paper border border-ink/10 p-3">
            <p className="text-sm font-semibold text-ink">{t('directory.field.open')}</p>
            <TimeRuleFields
              value={draft.open}
              onChange={(patch) => set({ open: { ...draft.open, ...patch } })}
              idPrefix="hours-open"
              radioName="hours-open-type"
              fixedTimeLabel={t('directory.field.open')}
              extraZmanim={hoursZmanim}
            />
          </div>
          <div className="space-y-3 rounded-[10px] bg-paper border border-ink/10 p-3">
            <p className="text-sm font-semibold text-ink">{t('directory.field.close')}</p>
            <TimeRuleFields
              value={draft.close}
              onChange={(patch) => set({ close: { ...draft.close, ...patch } })}
              idPrefix="hours-close"
              radioName="hours-close-type"
              fixedTimeLabel={t('directory.field.close')}
              extraZmanim={hoursZmanim}
            />
            <p className="text-xs text-ash">{t('directory.admin.hours.afterMidnight')}</p>
          </div>
        </>
      )}

      <BilingualField
        labelHe={t('directory.field.noteHe')}
        labelEn={t('directory.field.noteEn')}
        valueHe={draft.noteHe}
        valueEn={draft.noteEn}
        onChangeHe={(noteHe) => set({ noteHe })}
        onChangeEn={(noteEn) => set({ noteEn })}
        maxLength={LISTING_LIMITS.noteLength}
        placeholderHe={t('directory.admin.hours.notePlaceholderHe')}
        placeholderEn={t('directory.admin.hours.notePlaceholderEn')}
      />

      {draft.kind !== 'date' && (
        <>
          <SelectField<HoursSeasonMode>
            label={t('directory.admin.hours.seasonTitle')}
            info={t('directory.admin.hours.seasonHint')}
            value={draft.season}
            onChange={(season) => set({ season })}
            options={HOURS_SEASON_MODES.map((m) => ({ value: m, label: t(`directory.admin.hours.season.${m}`) }))}
          />
          {draft.season === 'dates' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="hours-from" className="block text-sm font-medium text-ink mb-1">
                  {t('directory.field.validFrom')}
                </label>
                <input
                  id="hours-from"
                  type="date"
                  value={draft.validFrom}
                  onChange={(e) => set({ validFrom: e.target.value })}
                  className="w-full min-h-[44px] px-3 py-2 rounded-[10px] border border-ink bg-paper text-ink text-sm"
                />
              </div>
              <div>
                <label htmlFor="hours-to" className="block text-sm font-medium text-ink mb-1">
                  {t('directory.field.validTo')}
                </label>
                <input
                  id="hours-to"
                  type="date"
                  value={draft.validTo}
                  onChange={(e) => set({ validTo: e.target.value })}
                  className="w-full min-h-[44px] px-3 py-2 rounded-[10px] border border-ink bg-paper text-ink text-sm"
                />
              </div>
            </div>
          )}
        </>
      )}

      <ErrorList errors={errors} />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="min-h-[44px] px-5 rounded-[10px] bg-ink text-paper text-sm font-medium hover:opacity-90 disabled:opacity-40"
        >
          {saving ? t('directory.admin.saving') : t('directory.admin.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-[44px] px-4 rounded-[10px] text-sm text-ink hover:opacity-70"
        >
          {t('directory.admin.cancel')}
        </button>
      </div>
    </div>
  );
}
